import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useIssueInvoice } from "@/lib/finance/finance-hooks";
import { fmtFCFA } from "@/lib/format";
import { apiError, Field, inputStyle, VAT_OPTIONS } from "./_ui";

interface DraftLine {
	designation: string;
	quantity: number;
	unitPriceHt: number;
	discountPct: number;
	vatRate: number;
}

const round = (v: number) => (v >= 0 ? Math.round(v) : -Math.round(-v));
const lineHt = (l: DraftLine) =>
	round(l.quantity * l.unitPriceHt * (1 - l.discountPct / 100));
const lineVat = (l: DraftLine) => round(lineHt(l) * l.vatRate);

/** FIN-03 — Émission d'une facture (B2C / B2B / proforma). */
export function AdminInvoiceCreatePage() {
	const navigate = useNavigate();
	const issue = useIssueInvoice();
	const [error, setError] = useState<string | null>(null);

	const [type, setType] = useState<"B2C" | "B2B" | "PROFORMA">("B2B");
	const [cust, setCust] = useState({
		name: "",
		niu: "",
		address: "",
		city: "",
		phone: "",
		email: "",
	});
	const [issuedAt, setIssuedAt] = useState(
		new Date().toISOString().slice(0, 10),
	);
	const [dueAt, setDueAt] = useState("");
	const [analyticHub, setHub] = useState("HUB-YDE");
	const [lines, setLines] = useState<DraftLine[]>([
		{
			designation: "",
			quantity: 1,
			unitPriceHt: 0,
			discountPct: 0,
			vatRate: 0.1925,
		},
	]);

	const totals = useMemo(() => {
		const ht = lines.reduce((s, l) => s + lineHt(l), 0);
		const vat = lines.reduce((s, l) => s + lineVat(l), 0);
		return { ht, vat, ttc: ht + vat };
	}, [lines]);

	const updateLine = (i: number, patch: Partial<DraftLine>) =>
		setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
	const addLine = () =>
		setLines((ls) => [
			...ls,
			{
				designation: "",
				quantity: 1,
				unitPriceHt: 0,
				discountPct: 0,
				vatRate: 0.1925,
			},
		]);
	const removeLine = (i: number) =>
		setLines((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : ls));

	const submit = () => {
		setError(null);
		if (!cust.name.trim()) return setError("Nom du client requis");
		if (lines.some((l) => !l.designation.trim() || l.unitPriceHt <= 0))
			return setError(
				"Chaque ligne doit avoir une désignation et un PU HT > 0",
			);
		issue.mutate(
			{
				type,
				customerSnapshot: {
					name: cust.name.trim(),
					niu: cust.niu.trim() || undefined,
					address: cust.address.trim() || undefined,
					city: cust.city.trim() || undefined,
					phone: cust.phone.trim() || undefined,
					email: cust.email.trim() || undefined,
					type: type === "B2B" ? "B2B" : "B2C",
				},
				issuedAt,
				dueAt: dueAt || undefined,
				analyticHub,
				lines: lines.map((l) => ({
					designation: l.designation.trim(),
					quantity: l.quantity,
					unitPriceHt: l.unitPriceHt,
					discountPct: l.discountPct,
					vatRate: l.vatRate,
				})),
			},
			{
				onSuccess: (inv) => navigate(`/finance/factures/${inv.id}`),
				onError: (e) => setError(apiError(e)),
			},
		);
	};

	return (
		<>
			<PageHead
				title="Émettre une facture"
				sub="Numérotation séquentielle automatique · TVA calculée ligne par ligne"
				actions={
					<AdminButton
						variant="ghost"
						onClick={() => navigate("/finance/factures")}
					>
						<AdminIcon n="arrow" s={14} /> Retour
					</AdminButton>
				}
			/>

			{error && <AlertBanner variant="danger" title={error} />}

			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
					gap: 16,
				}}
			>
				<Card>
					<CardHead title="Type & client" />
					<CardBody>
						<div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
							{(["B2C", "B2B", "PROFORMA"] as const).map((t) => (
								<button
									key={t}
									className={type === t ? "btn-primary" : "btn-ghost"}
									onClick={() => setType(t)}
									style={{ flex: 1 }}
								>
									{t}
								</button>
							))}
						</div>
						<div style={{ display: "grid", gap: 10 }}>
							<Field
								label={type === "B2B" ? "Raison sociale *" : "Nom client *"}
							>
								<input
									style={inputStyle}
									value={cust.name}
									onChange={(e) => setCust({ ...cust, name: e.target.value })}
								/>
							</Field>
							{type === "B2B" && (
								<Field label="NIU (obligatoire si TTC > 500 000 XAF)">
									<input
										style={inputStyle}
										value={cust.niu}
										placeholder="M032412345678X"
										onChange={(e) => setCust({ ...cust, niu: e.target.value })}
									/>
								</Field>
							)}
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "2fr 1fr",
									gap: 10,
								}}
							>
								<Field label="Adresse">
									<input
										style={inputStyle}
										value={cust.address}
										onChange={(e) =>
											setCust({ ...cust, address: e.target.value })
										}
									/>
								</Field>
								<Field label="Ville">
									<input
										style={inputStyle}
										value={cust.city}
										onChange={(e) => setCust({ ...cust, city: e.target.value })}
									/>
								</Field>
							</div>
						</div>
					</CardBody>
				</Card>

				<Card>
					<CardHead title="Dates & analytique" />
					<CardBody>
						<div style={{ display: "grid", gap: 10 }}>
							<Field label="Date d'émission">
								<input
									type="date"
									style={inputStyle}
									value={issuedAt}
									onChange={(e) => setIssuedAt(e.target.value)}
								/>
							</Field>
							<Field label="Échéance (B2B)">
								<input
									type="date"
									style={inputStyle}
									value={dueAt}
									onChange={(e) => setDueAt(e.target.value)}
								/>
							</Field>
							<Field label="Hub analytique">
								<select
									style={inputStyle}
									value={analyticHub}
									onChange={(e) => setHub(e.target.value)}
								>
									<option value="HUB-YDE">HUB-YDE (Yaoundé)</option>
									<option value="HUB-DLA">HUB-DLA (Douala)</option>
								</select>
							</Field>
						</div>
					</CardBody>
				</Card>
			</div>

			<Card style={{ marginTop: 16 }}>
				<CardHead
					title="Lignes de facture"
					right={
						<AdminButton variant="ghost" size="sm" onClick={addLine}>
							<AdminIcon n="plus" s={13} /> Ligne
						</AdminButton>
					}
				/>
				<CardBody>
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th style={{ minWidth: 200 }}>Désignation</th>
									<th style={{ width: 80 }}>Qté</th>
									<th style={{ width: 120 }}>PU HT</th>
									<th style={{ width: 90 }}>Remise %</th>
									<th style={{ width: 150 }}>TVA</th>
									<th className="right">Total HT</th>
									<th className="right">TTC</th>
									<th />
								</tr>
							</thead>
							<tbody>
								{lines.map((l, i) => (
									<tr key={i}>
										<td>
											<input
												style={inputStyle}
												value={l.designation}
												onChange={(e) =>
													updateLine(i, { designation: e.target.value })
												}
											/>
										</td>
										<td>
											<input
												type="number"
												min={0}
												style={inputStyle}
												value={l.quantity}
												onChange={(e) =>
													updateLine(i, { quantity: Number(e.target.value) })
												}
											/>
										</td>
										<td>
											<input
												type="number"
												min={0}
												style={inputStyle}
												value={l.unitPriceHt}
												onChange={(e) =>
													updateLine(i, { unitPriceHt: Number(e.target.value) })
												}
											/>
										</td>
										<td>
											<input
												type="number"
												min={0}
												max={100}
												style={inputStyle}
												value={l.discountPct}
												onChange={(e) =>
													updateLine(i, { discountPct: Number(e.target.value) })
												}
											/>
										</td>
										<td>
											<select
												style={inputStyle}
												value={l.vatRate}
												onChange={(e) =>
													updateLine(i, { vatRate: Number(e.target.value) })
												}
											>
												{VAT_OPTIONS.map((o) => (
													<option key={o.value} value={o.value}>
														{o.label}
													</option>
												))}
											</select>
										</td>
										<td className="right tn">{fmtFCFA(lineHt(l))}</td>
										<td className="right tn">
											<strong>{fmtFCFA(lineHt(l) + lineVat(l))}</strong>
										</td>
										<td>
											<button
												className="btn-ghost btn-sm"
												onClick={() => removeLine(i)}
												aria-label="Supprimer la ligne"
											>
												<AdminIcon n="close" s={13} />
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							marginTop: 16,
						}}
					>
						<div style={{ minWidth: 280, display: "grid", gap: 6 }}>
							<div style={{ display: "flex", justifyContent: "space-between" }}>
								<span className="muted">Total HT</span>
								<span className="tn">{fmtFCFA(totals.ht)}</span>
							</div>
							<div style={{ display: "flex", justifyContent: "space-between" }}>
								<span className="muted">TVA</span>
								<span className="tn">{fmtFCFA(totals.vat)}</span>
							</div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									borderTop: "1px solid var(--a-border)",
									paddingTop: 6,
								}}
							>
								<strong>TOTAL TTC</strong>
								<strong className="tn">{fmtFCFA(totals.ttc)}</strong>
							</div>
						</div>
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 10,
							marginTop: 16,
							alignItems: "center",
						}}
					>
						{type === "B2B" && totals.ttc > 500_000 && !cust.niu.trim() && (
							<Tag variant="warning" noDot>
								NIU requis pour ce montant
							</Tag>
						)}
						<AdminButton
							variant="primary"
							onClick={submit}
							disabled={issue.isPending}
						>
							<AdminIcon n="check" s={14} />{" "}
							{issue.isPending ? "Émission…" : `Émettre (${type})`}
						</AdminButton>
					</div>
				</CardBody>
			</Card>
		</>
	);
}
